/**
 * Formats large numbers into user-friendly Indian units (Lakhs & Crores)
 *
 * Examples:
 *   15,069,025 -> "1.51 Cr"
 *   7,50,000   -> "7.5 L"
 *   45,000     -> "45,000"
 */
export function formatIndianCompact(
  val: number,
  options?: { suffix?: string; isCurrency?: boolean; decimals?: number }
): string {
  if (val == null || isNaN(val)) return '0'
  const prefix = options?.isCurrency ? '₹' : ''
  const suffix = options?.suffix ? ` ${options.suffix}` : ''
  const abs = Math.abs(val)
  const sign = val < 0 ? '-' : ''
  const decimals = options?.decimals ?? 2

  if (abs >= 10000000) {
    // 1 Crore = 1,00,00,000
    const cr = (val / 10000000).toFixed(decimals)
    const cleaned = parseFloat(cr).toString()
    return `${sign}${prefix}${cleaned} Cr${suffix}`
  }
  if (abs >= 100000) {
    // 1 Lakh = 1,00,000
    const l = (val / 100000).toFixed(decimals)
    const cleaned = parseFloat(l).toString()
    return `${sign}${prefix}${cleaned} L${suffix}`
  }

  // Under 1 Lakh: standard Indian comma notation
  return `${sign}${prefix}${val.toLocaleString('en-IN')}${suffix}`
}

/**
 * Returns full Indian formatted number (e.g. ₹1,50,69,025)
 */
export function formatIndianFull(
  val: number,
  options?: { suffix?: string; isCurrency?: boolean }
): string {
  if (val == null || isNaN(val)) return '0'
  const prefix = options?.isCurrency ? '₹' : ''
  const suffix = options?.suffix ? ` ${options.suffix}` : ''
  return `${prefix}${val.toLocaleString('en-IN')}${suffix}`
}

/**
 * Derives a monthly units figure from an annual (per-annum) units figure,
 * spread evenly across 12 months.
 */
export function annualToMonthlyUnits(annualKWh?: number | null): number {
  if (!annualKWh || isNaN(annualKWh)) return 0
  return annualKWh / 12
}

/**
 * Formats a kWh units figure compactly (kWh / MWh / GWh).
 */
export function formatUnitsKWh(kwh?: number | null): string {
  if (kwh == null || isNaN(kwh)) return '0 kWh'
  const abs = Math.abs(kwh)
  if (abs >= 1e6) return `${(kwh / 1e6).toFixed(2)} GWh`
  if (abs >= 1e3) return `${(kwh / 1e3).toFixed(1)} MWh`
  return `${Math.round(kwh).toLocaleString('en-IN')} kWh`
}

/**
 * Formats an annual units figure alongside its derived monthly equivalent,
 * e.g. "12,00,000 kWh/yr (~1.00 L kWh/mo)".
 */
export function formatAnnualWithMonthly(annualKWh?: number | null): string {
  const annual = annualKWh || 0
  const monthly = annualToMonthlyUnits(annual)
  return `${formatUnitsKWh(annual)}/yr (~${formatUnitsKWh(monthly)}/mo)`
}
